import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarIcon, ArrowUpRight, Mail, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import Section, { SectionHeader, FadeIn } from "@/components/Section";
import { BRAND } from "@/lib/brand";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30",
  "11:00", "11:30", "13:00", "13:30",
  "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30",
];

const TARGET_OPTIONS = [
  { v: "azubi", l: "Azubi" },
  { v: "fachkraft", l: "Fachkraft" },
  { v: "fuehrungskraft", l: "Führungskraft" },
  { v: "unternehmen", l: "Unternehmen / Firma" },
  { v: "sonstiges", l: "Sonstiges" },
];

const SERVICE_OPTIONS = [
  "Unverbindliches Erstgespräch",
  "Azubi-Coaching",
  "Coaching für Berufseinsteiger",
  "Karriere- & Entwicklungscoaching",
  "Coaching für Fachkräfte",
  "Führungskräfteentwicklung",
  "Kommunikation & Auftreten",
  "Interim-Geschäftsführung",
  "Projektmandate",
  "Speaker & Fachvorträge",
  "Messen & Events",
];

function readTopicFromURL() {
  if (typeof window === "undefined") return "";
  const t = new URLSearchParams(window.location.search).get("topic") || "";
  return SERVICE_OPTIONS.includes(t) ? t : "";
}

export default function ContactPage() {
  const [form, setForm] = useState(() => ({
    name: "",
    email: "",
    phone: "",
    target_group: "",
    service: readTopicFromURL(),
    notes: "",
  }));
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();

  // React to topic changes when navigating between ?topic= values without full reload
  useEffect(() => {
    const topic = searchParams.get("topic") || "";
    if (topic && SERVICE_OPTIONS.includes(topic)) {
      setForm((f) => (f.service === topic ? f : { ...f, service: topic }));
    }
  }, [searchParams]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !date || !time) {
      toast.error("Bitte Name, E-Mail, Datum und Uhrzeit angeben.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        target_group: form.target_group || null,
        service: form.service || null,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        notes: form.notes || null,
      };
      await axios.post(`${API}/bookings`, payload);
      setSuccess(true);
      toast.success("Anfrage erfolgreich gesendet. Wir melden uns persönlich.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    <div data-testid="contact-page" className="pt-36 lg:pt-44">
      <Section padded={false} className="pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <FadeIn>
              <div className="overline mb-6">Kontakt</div>
              <h1 className="font-display text-[44px] sm:text-[60px] lg:text-[76px] leading-[0.98] tracking-[-0.035em] text-white">
                Ihr nächstes
                <br />
                <span className="font-editorial italic text-white/90">
                  Gespräch mit Wirkung.
                </span>
              </h1>
              <p className="mt-8 text-white/65 text-lg leading-relaxed max-w-xl">
                Das Erstgespräch ist kostenfrei und unverbindlich. Wählen Sie
                einen Wunschtermin und ich melde mich persönlich zurück.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Coaching", "Interim-GF", "Projektmandate", "Speaker", "Messen"].map((t) => (
                  <span
                    key={t}
                    className="text-[11.5px] uppercase tracking-[0.18em] text-white/55 border border-white/10 rounded-full px-3 py-1.5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </FadeIn>

            <FadeIn delay={0.1}>
              <div className="mt-12 space-y-5">
                <div className="flex items-start gap-4 text-white/80">
                  <MapPin
                    size={20}
                    className="mt-0.5 text-brand-400 flex-shrink-0"
                    strokeWidth={1.5}
                  />
                  <div>
                    <div className="overline !text-[10px] mb-1.5">Adresse</div>
                    <div className="text-[15.5px] leading-relaxed">
                      {BRAND.company} — {BRAND.name}
                      <br />
                      {BRAND.address.street}, {BRAND.address.city}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 text-white/80">
                  <Phone
                    size={20}
                    className="mt-0.5 text-brand-400 flex-shrink-0"
                    strokeWidth={1.5}
                  />
                  <div>
                    <div className="overline !text-[10px] mb-1.5">Telefon</div>
                    <a href={BRAND.phoneHref} className="text-[15.5px] link-underline">
                      {BRAND.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4 text-white/80">
                  <Mail
                    size={20}
                    className="mt-0.5 text-brand-400 flex-shrink-0"
                    strokeWidth={1.5}
                  />
                  <div>
                    <div className="overline !text-[10px] mb-1.5">E-Mail</div>
                    <a href={`mailto:${BRAND.email}`} className="text-[15.5px] link-underline">
                      {BRAND.email}
                    </a>
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="mt-14 aspect-[4/5] rounded-[12px] overflow-hidden border border-white/10 max-w-md">
                <img
                  src={BRAND.photos.coaching}
                  alt="Alessandro Costantino im Gespräch"
                  className="w-full h-full object-cover img-editorial"
                />
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-6">
            <FadeIn delay={0.1}>
              <div className="surface p-8 lg:p-10">
                {success ? (
                  <div
                    data-testid="contact-success-state"
                    className="py-14 text-center"
                  >
                    <CheckCircle2
                      size={48}
                      className="text-brand-400 mx-auto"
                      strokeWidth={1.25}
                    />
                    <h3 className="mt-6 font-display text-3xl text-white tracking-tight">
                      Anfrage gesendet.
                    </h3>
                    <p className="mt-4 text-white/60 text-[15.5px] leading-relaxed max-w-md mx-auto">
                      Vielen Dank. Ich melde mich persönlich per E-Mail oder
                      Telefon, um den Termin zu bestätigen.
                    </p>
                    <button
                      data-testid="contact-new-request"
                      onClick={() => {
                        setSuccess(false);
                        setForm({
                          name: "",
                          email: "",
                          phone: "",
                          target_group: "",
                          service: "",
                          notes: "",
                        });
                        setDate(undefined);
                        setTime("");
                      }}
                      className="btn-ghost mt-10 mx-auto"
                    >
                      Neue Anfrage stellen
                    </button>
                  </div>
                ) : (
                  <form
                    data-testid="contact-form"
                    onSubmit={submit}
                    className="space-y-6"
                  >
                    <div className="overline">Termin-Anfrage</div>
                    <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-white leading-tight">
                      Wunschtermin auswählen.
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="text-white/80 text-[13px] tracking-tight mb-2 block">
                          Name <span className="text-brand-400">*</span>
                        </Label>
                        <Input
                          id="name"
                          data-testid="contact-input-name"
                          value={form.name}
                          onChange={(e) => update("name", e.target.value)}
                          placeholder="Vor- und Nachname"
                          className="bg-[#0B1324] border-white/[0.1] text-white h-12"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-white/80 text-[13px] tracking-tight mb-2 block">
                          E-Mail <span className="text-brand-400">*</span>
                        </Label>
                        <Input
                          id="email"
                          data-testid="contact-input-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          placeholder="name@email.de"
                          className="bg-[#0B1324] border-white/[0.1] text-white h-12"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="phone" className="text-white/80 text-[13px] tracking-tight mb-2 block">
                          Telefon (optional)
                        </Label>
                        <Input
                          id="phone"
                          data-testid="contact-input-phone"
                          value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          placeholder="+49 …"
                          className="bg-[#0B1324] border-white/[0.1] text-white h-12"
                        />
                      </div>
                      <div>
                        <Label className="text-white/80 text-[13px] tracking-tight mb-2 block">
                          Zielgruppe
                        </Label>
                        <Select
                          value={form.target_group}
                          onValueChange={(v) => update("target_group", v)}
                        >
                          <SelectTrigger
                            data-testid="contact-select-target"
                            className="bg-[#0B1324] border-white/[0.1] text-white h-12"
                          >
                            <SelectValue placeholder="Bitte wählen" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#13203B] border-white/10 text-white">
                            {TARGET_OPTIONS.map((o) => (
                              <SelectItem
                                key={o.v}
                                value={o.v}
                                className="focus:bg-white/5 focus:text-white"
                              >
                                {o.l}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-white/80 text-[13px] tracking-tight mb-2 block">
                        Interessiertes Format
                      </Label>
                      <Select
                        value={form.service}
                        onValueChange={(v) => update("service", v)}
                      >
                        <SelectTrigger
                          data-testid="contact-select-service"
                          className="bg-[#0B1324] border-white/[0.1] text-white h-12"
                        >
                          <SelectValue placeholder="Bitte wählen" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#13203B] border-white/10 text-white">
                          {SERVICE_OPTIONS.map((o) => (
                            <SelectItem
                              key={o}
                              value={o}
                              className="focus:bg-white/5 focus:text-white"
                            >
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label className="text-white/80 text-[13px] tracking-tight mb-2 block">
                          Wunschdatum <span className="text-brand-400">*</span>
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              data-testid="contact-date-trigger"
                              className="w-full h-12 px-3 flex items-center justify-between rounded-md bg-[#0B1324] border border-white/[0.1] text-[14.5px] text-left hover:border-white/20 transition"
                            >
                              <span className={date ? "text-white" : "text-white/40"}>
                                {date
                                  ? format(date, "EEEE, d. MMMM yyyy", { locale: de })
                                  : "Datum wählen"}
                              </span>
                              <CalendarIcon size={16} className="text-white/50" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            className="bg-[#13203B] border-white/10 p-0"
                          >
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={setDate}
                              locale={de}
                              weekStartsOn={1}
                              disabled={(d) => {
                                const now = new Date();
                                now.setHours(0, 0, 0, 0);
                                return d < now || d.getDay() === 0;
                              }}
                              className="text-white"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="text-white/80 text-[13px] tracking-tight mb-2 block">
                          Wunschuhrzeit <span className="text-brand-400">*</span>
                        </Label>
                        <Select value={time} onValueChange={setTime}>
                          <SelectTrigger
                            data-testid="contact-select-time"
                            className="bg-[#0B1324] border-white/[0.1] text-white h-12"
                          >
                            <SelectValue placeholder="Uhrzeit wählen" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#13203B] border-white/10 text-white max-h-64">
                            {TIME_SLOTS.map((t) => (
                              <SelectItem
                                key={t}
                                value={t}
                                className="focus:bg-white/5 focus:text-white"
                              >
                                {t} Uhr
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="notes" className="text-white/80 text-[13px] tracking-tight mb-2 block">
                        Ihre Nachricht
                      </Label>
                      <Textarea
                        id="notes"
                        data-testid="contact-input-notes"
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                        placeholder="Worum geht es? Kurz in eigenen Worten."
                        rows={5}
                        className="bg-[#0B1324] border-white/[0.1] text-white resize-none"
                      />
                    </div>

                    <div className="text-[12px] text-white/45 leading-relaxed">
                      Mit dem Absenden bestätigen Sie, dass Ihre Angaben zur
                      Bearbeitung der Anfrage verarbeitet werden dürfen. Details:{" "}
                      <a href="/datenschutz" className="link-underline text-white/60">
                        Datenschutzerklärung
                      </a>
                      .
                    </div>

                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={submitting}
                      className="btn-primary w-full justify-center disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" /> Wird gesendet …
                        </>
                      ) : (
                        <>
                          Anfrage senden <ArrowUpRight size={18} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>
    </div>
  );
}
